import {
  compactFormat,
  dataLabelsCompact,
  dataLabelsDecimals,
  dataLabelsFlipHorizontally,
  dataLabelsHideThreshold,
  dataLabelsSizes,
  dimension,
  dimensionSelector,
  inverse,
  legendPosition,
  palette,
  showDataLabels,
  showItems,
} from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { Config } from "@/types/charts";

const decompositionMetric = {
  default: "campaign_kpi",
  type: "select",
  label: "Decomposition Metric 1",
  dictionary: "dimensionMetrics",
  apiParameter: "dimension",
  gridPosition: [0, 0],
  tooltipOrder: 0,
};

const decompositionMetricSecondary = {
  default: "channel",
  type: "select",
  label: "Decomposition Metric 2",
  dictionary: "dimensionMetrics",
  apiParameter: "dimension",
  gridPosition: [1, 0],
  tooltipOrder: 1,
};

const lineMetric = {
  default: "impressions",
  type: "select",
  label: "Display Metric (Line)",
  dictionary: "displayMetrics",
  gridPosition: [3, 0],
  tooltipOrder: 3,
};

const axisTitles = {
  xAxisName: {
    default: "",
    type: "text",
    label: "Horizontal Axis Title 1",
    gridPosition: [0, 1],
    forAxis: "decompositionMetric",
  },
  xAxisSecondaryName: {
    default: "",
    type: "text",
    label: "Horizontal Axis Title 2",
    gridPosition: [1, 1],
    forAxis: "decompositionMetricSecondary",
  },
  yAxisName: {
    default: "",
    type: "text",
    label: "Vertical Axis Title",
    gridPosition: [2, 1],
    forAxis: "displayMetric",
  },
};

const stackedColumnLineDecompositionSettings = {
  palette,
  decompositionMetric,
  decompositionMetricSecondary,
  displayMetric: {
    default: "spend",
    type: "select",
    label: "Display Metric (Stacked Bars)",
    dictionary: "displayMetrics",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  lineMetric,
  showItems: { ...showItems, gridPosition: [4, 0] },
  legendPosition: { ...legendPosition, gridPosition: [5, 0] },
  compactFormat: { ...compactFormat, gridPosition: [6, 0] },
  dimensionSelector: { ...dimensionSelector, gridPosition: [7, 0] },
  ...axisTitles,
};

const stackedColumnLineDecompositionPlanExploreSettings = {
  ...stackedColumnLineDecompositionSettings,
  showDataLabels: { ...showDataLabels, gridPosition: [8, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [8, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [9, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [9, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [10, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [10, 0] },
};

const stackedColumnLineDecompositionPlanSettings = {
  palette,
  decompositionMetric,
  decompositionMetricSecondary,
  displayMetric: {
    default: "impressions",
    type: "select",
    label: "Display Metric (Stacked Bars)",
    dictionary: "displayMetrics",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  lineMetric: { ...lineMetric, default: "channel_budget" },
  showItems: { ...showItems, gridPosition: [4, 0] },
  legendPosition: { ...legendPosition, gridPosition: [5, 0] },
  compactFormat: { ...compactFormat, gridPosition: [6, 0] },
  dimensionSelector: { ...dimensionSelector, gridPosition: [7, 0] },
  showDataLabels: { ...showDataLabels, gridPosition: [8, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [8, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [9, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [9, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [10, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [10, 0] },
  ...axisTitles,
};

const stackedColumnLineDecompositionDualSettings = {
  palette,
  displayMetricSource: { default: "explore", dictionary: "metricSources" },
  lineMetricSource: { default: "plan", dictionary: "metricSources" },
  decompositionMetric: {
    default: "campaign_kpi",
    type: "select",
    label: "Grouping Dimension 1 (Project + Explore)",
    dictionary: "dualDimensionMetrics",
    apiParameter: "dimension",
    gridPosition: [0, 0],
    tooltipOrder: 0,
  },
  decompositionMetricSecondary: {
    default: "channel",
    type: "select",
    label: "Grouping Dimension 2 (Stacked Bars)",
    dictionary: "dualDimensionMetrics",
    apiParameter: "dimension",
    gridPosition: [1, 0],
    tooltipOrder: 1,
  },
  displayMetric: {
    default: "impressions",
    defaultBySource: { plan: "impressions", explore: "impressions" },
    type: "select",
    label: "Display Metric (Stacked Bars)",
    dictionary: "displayMetrics",
    sourceFrom: "displayMetricSource",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  lineMetric: {
    default: "channel_budget",
    defaultBySource: { plan: "channel_budget", explore: "spend" },
    type: "select",
    label: "Display Metric (Line)",
    dictionary: "displayMetrics",
    sourceFrom: "lineMetricSource",
    gridPosition: [3, 0],
    tooltipOrder: 3,
  },
  legendPosition: { ...legendPosition, gridPosition: [4, 0] },
  showDataLabels: { ...showDataLabels, gridPosition: [5, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [5, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [6, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [6, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [7, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [7, 0] },
  ...axisTitles,
};

export default <Config>{
  family: [FAMILY.COMPARISON],
  name: "stackedColumnLineDecompositionChart",
  title: "Stacked Column & Line Type 1",
  defaultHeight: 6,
  controls: [{ ...dimension, metricName: "decompositionMetricSecondary" }, inverse],
  dataModes: ["project", "explore", "dual"],
  settings: stackedColumnLineDecompositionSettings,
  planSettings: stackedColumnLineDecompositionPlanSettings,
  planExploreSettings: stackedColumnLineDecompositionPlanExploreSettings,
  planDualSettings: stackedColumnLineDecompositionDualSettings,
};
